import React, { useEffect, useState } from 'react';
import { Menu } from './components/Menu';
import SearchPage from './components/SearchPage';
import BrowsePage from './components/BrowsePage';
import DossierPage from './components/DossierPage';
import ClassesPage from './components/ClassesPage';
import UserGroupAdmin from './components-admin/UserGroupAdmin';
import FieldDefinitionsAdmin from './components-admin/FieldDefinitionsAdmin';
import DrawerAdmin from './components-admin/DrawerAdmin';
import DocumentTypeAdmin from './components-admin/DocumentTypeAdmin';
import UserAdmin from './components-admin/UserAdmin';
import LogsAdmin from './components-admin/LogsAdmin';
import Login from './components/Login';
import { initializeMsal, getActiveAccount, getAccessTokenSilent, ensureReauth } from './auth/msal';
// Avoid statically importing @azure/msal-browser types to keep msal library code-splittable
type AccountInfo = any;
import { http } from './api/http';
import { getDrawers } from './api/drawers';

interface PingResponse { message: string; }
interface MeResponse { id: number; userName: string; groups: { id: number; name: string; }[]; }

const LOCAL_KEY_ID = 'selectedDrawerId';
const LOCAL_KEY_NAME = 'selectedDrawerName';
const DEFAULT_ROUTE = 'drawers';
const ADMIN_ROUTES = [
  'admin-users',
  'admin-groups',
  'admin-drawers-admin',
  'admin-fielddefinitions',
  'admin-documenttypes',
  'admin-logs'
];

const getInitialRoute = () => {
  const hash = window.location.hash || '';
  if (hash.toLowerCase().includes('#search')) return 'search';
  if (hash.toLowerCase().includes('#drawers')) return 'drawers';
  if (hash.toLowerCase().includes('#dossier')) return 'dossier';
  // If querystring has drawerid or any search params -> go to search
  const params = new URLSearchParams(window.location.search || '');
  //if (params.has('drawerid') || Array.from(params.keys()).length > 0) return 'search';
  return window.location.hash.replace('#', '') || DEFAULT_ROUTE;
};

const getInitialDrawerId = () => {
  const params = new URLSearchParams(window.location.search || '');
  const fromUrl = params.get('drawerid');
  if (fromUrl && !isNaN(Number(fromUrl))) return Number(fromUrl);
  const id = localStorage.getItem(LOCAL_KEY_ID);
  return id ? Number(id) : null;
};
const getInitialDrawerName = () => localStorage.getItem(LOCAL_KEY_NAME) || null;

function parseDrawerPages(raw: any): string[] {
  const s = String(raw ?? '').trim();
  if (!s) return [];
  return s.split(',').map(p => p.trim()).filter(Boolean);
}

type DrawerPageKey = 'browse' | 'search' | 'dossier';
const DEFAULT_PAGES: DrawerPageKey[] = ['browse', 'search', 'dossier'];

function isDrawerPageKey(x: string): x is DrawerPageKey {
  return x === 'browse' || x === 'search' || x === 'dossier';
}

function getDefaultPageForDrawer(drawer: any | null): string {
  const pages = parseDrawerPages(drawer?.drawerPages ?? drawer?.DrawerPages);
  const known = pages.filter(p => p === 'browse' || p === 'search' || p === 'dossier');
  return known[0] ?? 'search';
}

const App: React.FC = () => {
  const [apiResult, setApiResult] = useState<string>('Loading...');
  const [error, setError] = useState<string | null>(null);
  const [selectedDrawerId, setSelectedDrawerId] = useState<number | null>(getInitialDrawerId());
  const [selectedDrawerName, setSelectedDrawerName] = useState<string | null>(getInitialDrawerName());
  const [selectedDrawer, setSelectedDrawer] = useState<any | null>(null);
  const [drawerPages, setDrawerPages] = useState<string[]>([]);
  const [route, setRoute] = useState<string>(getInitialRoute());
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  // Whether the admin check (/api/user/me) has resolved yet. The deny-guard must
  // wait for this, otherwise an F5 on an admin route redirects a real admin away
  // before isAdmin has been determined.
  const [adminChecked, setAdminChecked] = useState(false);
  const [adminDeniedError, setAdminDeniedError] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [drawersCache, setDrawersCache] = useState<any[]>([]);

  // init msal redirect handling and perform a silent token check before showing UI
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await initializeMsal();
        const existing = getActiveAccount();
        let redirected = false;
        if (existing) {
          // Try to acquire a token silently to confirm we can call the API before showing UI
          try {
            const token = await getAccessTokenSilent();
            if (token && mounted) {
              setAccount(existing);
              setAuthError(null);
            } else {
              // silent failed without throwing - try interactive redirect
              try { await ensureReauth(false); redirected = true; } catch (e) { console.warn('Interactive reauth failed', e); }
            }
          } catch (e) {
            // Silent token acquisition failed; attempt interactive redirect to authenticate automatically
            console.warn('Silent token acquisition failed during startup', e);
            try { await ensureReauth(false); redirected = true; } catch (re) { console.warn('Interactive reauth failed', re); }
          }
        } else {
          // No active account: try to start interactive authentication automatically
          try { await ensureReauth(false); redirected = true; } catch (e) { console.warn('Interactive reauth failed', e); }
        }

        if (!redirected && mounted) setAuthChecked(true);
      } catch (e) {
        console.error('MSAL initialization error', e);
        if (mounted) setAuthChecked(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Acquire ping (only when authenticated) - now via http helper so Authorization header is added
  useEffect(() => {
    if (!account) return;
    http<PingResponse>('/api/ping')
      .then(d => { setApiResult(d.message); setError(null); setAuthError(null); })
      .catch(e => {
        if (e.message.includes('403') || e.message.toLowerCase().includes('not authorized')) {
            setAuthError('Not authorized. Ask an administrator to add your email address to the system.');
        } else if (e.message.startsWith('401')) {
            setError('Unauthorized (401).');
        } else setError(e.message);
      });
  }, [account]);

  // Determine admin by asking API for current user groups ("admin")
  useEffect(() => {
    if (!account) { setIsAdmin(false); setAdminChecked(false); return; }
    setAdminChecked(false);
    http<MeResponse>('/api/user/me')
      .then(me => { setIsAdmin(me.groups?.some(g => g.name?.toLowerCase() === 'admin') || false); })
      .catch(() => setIsAdmin(false))
      .finally(() => setAdminChecked(true));
  }, [account]);

  // Ensure navigating away from admin routes if not admin (after login or route change).
  // Only enforce once the admin check has resolved, so reloading (F5) on an admin
  // route doesn't bounce a real admin before isAdmin is known.
  useEffect(() => {
    if (adminChecked && !isAdmin && ADMIN_ROUTES.includes(route)) {
      setAdminDeniedError('You are not authorized for admin functions.');
      window.location.hash = DEFAULT_ROUTE;
      setRoute(DEFAULT_ROUTE);
    }
  }, [route, isAdmin, adminChecked]);

  useEffect(() => {
    const onHashChange = () => {
      const h = window.location.hash || '';
      if (h.toLowerCase().startsWith('#search')) setRoute('search');
      else setRoute(h.replace('#', '') || DEFAULT_ROUTE);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // React to URL search param changes (e.g., direct link with ?drawerid=..)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search || '');
    const fromUrl = params.get('drawerid');
    if (fromUrl && !isNaN(Number(fromUrl))) setSelectedDrawerId(Number(fromUrl));
  }, []);

  const handleDrawerSelect = (id: number, name: string) => {
    // When switching drawers, clear any existing search criteria from the URL.
    // We keep only drawerid and the current hash route.
    try {
      const params = new URLSearchParams();
      params.set('drawerid', String(id));
      const hash = window.location.hash || '';
      const newUrl = `${window.location.pathname}?${params.toString()}${hash}`;
      if (newUrl !== window.location.href) window.history.replaceState(null, '', newUrl);
    } catch {
      // ignore
    }

    setSelectedDrawerId(id);
    setSelectedDrawerName(name);
    localStorage.setItem(LOCAL_KEY_ID, String(id));
    localStorage.setItem(LOCAL_KEY_NAME, name);

    const h = (window.location.hash || '').replace('#', '');
    const keepRoute = h && h !== DEFAULT_ROUTE;
    if (!keepRoute) {
      const found = drawersCache.find(d => Number(d.id) === Number(id)) ?? null;
      const enabled = parseDrawerPages((found as any)?.drawerPages ?? (found as any)?.DrawerPages).filter(isDrawerPageKey);
      const allowed: DrawerPageKey[] = (enabled.length ? enabled : DEFAULT_PAGES);
      const target = allowed[0];
      window.location.hash = target;
      setRoute(target);
    }
  };

  const handleDrawerSelectAndGoSearch = (id: number, name: string) => {
    // Despite the legacy name, this should follow the drawer's configured first page.
    handleDrawerSelect(id, name);
  };

  const handleMenuRoute = (r: string) => { window.location.hash = r; setRoute(r); setAdminDeniedError(null); };

  // Load drawers once after auth so we can derive drawer settings immediately (prevents menu flicker)
  useEffect(() => {
    if (!account) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await getDrawers();
        if (!cancelled) setDrawersCache(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setDrawersCache([]);
      }
    })();
    return () => { cancelled = true; };
  }, [account]);

  // Keep selectedDrawer and drawerPages in sync with selectedDrawerId using cached drawers.
  useEffect(() => {
    if (!selectedDrawerId) { setSelectedDrawer(null); return; }
    const found = drawersCache.find(d => Number(d.id) === Number(selectedDrawerId)) ?? null;
    setSelectedDrawer(found);
    if (found) {
      setDrawerPages(parseDrawerPages((found as any)?.drawerPages ?? (found as any)?.DrawerPages).filter(isDrawerPageKey));
    }
  }, [selectedDrawerId, drawersCache]);

  // Existing effect: if drawer isn't in cache yet (first load), fetch and update cache.
  useEffect(() => {
    if (!account) return;
    if (!selectedDrawerId) return;
    if (drawersCache.some(d => Number(d.id) === Number(selectedDrawerId))) return;

    let cancelled = false;
    (async () => {
      try {
        const list = await getDrawers();
        if (cancelled) return;
        setDrawersCache(Array.isArray(list) ? list : []);
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [account, selectedDrawerId, drawersCache]);

  // Wait for auth check to complete before rendering anything
  if (!authChecked) return <div>Loading...</div>;

  // Not logged in yet
  if (!account) return <Login error={authError} />;

  // Logged in but backend rejects user (not in local DB) -> show login with error again
  if (authError) return <Login error={authError} />;

  const userName = account?.name || account?.username || null;
  const userId = (account?.idTokenClaims as any)?.oid || (account as any)?.localAccountId || null;

  return (
    <div className="app-container">
      <Menu selectedDrawerName={selectedDrawerName} onDrawerSelect={handleDrawerSelect} onRouteChange={handleMenuRoute} currentRoute={route} userName={userName} userId={userId} isAdmin={isAdmin} drawerPages={drawerPages} />
      <main className="app-main">
        {adminDeniedError && (
          <div style={{ background: '#ffe4e4', color: '#b00020', padding: '12px 16px', borderRadius: 6, marginBottom: 16, fontWeight: 500 }}>
            {adminDeniedError} Navigated to default page.
          </div>
        )}
        {/* Admin routes are gated on isAdmin, not just the redirect effect: while the
            check is pending show a placeholder; a confirmed non-admin renders nothing
            (the guard effect redirects them). */}
        {ADMIN_ROUTES.includes(route) && !isAdmin
          ? (adminChecked ? null : <div className="adm-status">Checking access…</div>)
          : route === 'admin-users' ? <UserAdmin />
          : route === 'admin-groups' ? <UserGroupAdmin />
          : route === 'admin-fielddefinitions' ? <FieldDefinitionsAdmin />
          : route === 'admin-drawers-admin' ? <DrawerAdmin />
          : route === 'admin-documenttypes' ? <DocumentTypeAdmin />
          : route === 'admin-logs' ? <LogsAdmin />
          : route === 'drawers' ? <ClassesPage onDrawerSelect={handleDrawerSelectAndGoSearch} />
          : route === 'search' ? <SearchPage drawerId={selectedDrawerId} drawerName={selectedDrawerName} metadataOption={(selectedDrawer as any)?.metadataOption ?? 0} />
          : route === 'browse' ? <BrowsePage drawerId={selectedDrawerId} drawerName={selectedDrawerName} metadataOption={(selectedDrawer as any)?.metadataOption ?? 0} />
          : route === 'dossier' ? <DossierPage drawerId={selectedDrawerId} drawerName={selectedDrawerName} metadataOption={(selectedDrawer as any)?.metadataOption ?? 0} />
          : <>
            <h1>WebReact Frontend</h1>
            <p>Welcome {account.username}</p>
            <section>
              <h2>API Test (/api/ping)</h2>
              {error && <p style={{ color: 'red' }}>Error: {error}</p>}
              {!error && <p>Response: {apiResult}</p>}
            </section>
          </>}
      </main>
    </div>
  );
};

export default App;
